import React, { useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Search, Save, X, ShoppingBag } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { adminCreateProduct, adminUpdateProduct, adminDeleteProduct } from '../lib/api';

const CATEGORIES = ['Legal Template', 'Book', 'Merchandise', 'Digital Download'];

const EMPTY = {
  name: '', description: '', price_inr: 0, category: 'Legal Template',
  image_url: '', in_stock: true,
};

const toPayload = (form) => ({
  ...form,
  price_inr: typeof form.price_inr === 'number' ? form.price_inr : parseFloat(form.price_inr) || 0,
});

const formatINR = (rupees) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(rupees || 0);

const submitLabel = (saving, isNew) => {
  if (saving) return 'Saving\u2026';
  if (isNew) return 'Add product';
  return 'Save changes';
};

const ProductsTab = ({ passcode, products, onChange }) => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      [p.name, p.description, p.category].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [products, query]);

  const startNew = () => { setForm(EMPTY); setEditing('new'); };
  const startEdit = (p) => {
    setForm({
      name: p.name || '', description: p.description || '', price_inr: p.price_inr || 0,
      category: p.category || 'Legal Template', image_url: p.image_url || '', in_stock: p.in_stock !== false,
    });
    setEditing(p);
  };
  const closeEditor = () => { if (saving) return; setEditing(null); setForm(EMPTY); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category.trim()) { toast.error('Name and category are required.'); return; }
    const price = parseFloat(form.price_inr);
    if (!price || price <= 0) { toast.error('Price must be a positive number.'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await adminCreateProduct(passcode, toPayload(form));
        onChange([created, ...products]);
        toast.success('Product added');
      } else {
        const updated = await adminUpdateProduct(passcode, editing.id, toPayload(form));
        onChange(products.map((p) => (p.id === editing.id ? updated : p)));
        toast.success('Product updated');
      }
      setEditing(null); setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save product.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await adminDeleteProduct(passcode, confirmDelete.id);
      onChange(products.filter((p) => p.id !== confirmDelete.id));
      toast.success(`\u201c${confirmDelete.name}\u201d removed`);
      setConfirmDelete(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete.');
    } finally { setDeletingId(null); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products\u2026" className="pl-10 h-11 border-[#E8DCC0] bg-white" />
        </div>
        <button onClick={startNew} className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="bg-white border border-[#E8DCC0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#EDE3CC] text-[#0F1B2D]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Product</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Price</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">In stock</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <tr key={p.id} className={`border-t border-[#F0E7CE] ${idx % 2 ? 'bg-[#FCF8EE]' : 'bg-white'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1B2D]">{p.name}</div>
                    {p.description ? <div className="text-xs text-[#5a5a5a] mt-1 line-clamp-2 max-w-md">{p.description}</div> : null}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#3a3a3a]">{p.category}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#3a3a3a] font-medium">{formatINR(p.price_inr)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${p.in_stock ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                      {p.in_stock ? 'In stock' : 'Out'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => setConfirmDelete(p)} disabled={deletingId === p.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors text-xs disabled:opacity-60">
                        {deletingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={5} className="text-center py-16 text-[#5a5a5a]">
                  <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-[#A36B1F]" strokeWidth={1.5} />
                  No products yet. Click \u201cAdd product\u201d to create your first one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="sm:max-w-xl bg-[#F8F2E4] border border-[#E8DCC0] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-besley text-2xl text-[#0F1B2D]">
              {editing === 'new' ? 'Add product' : `Edit ${editing?.name || 'product'}`}
            </DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">Prices are in INR (Indian Rupees), whole numbers only.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <Label className="text-[#0F1B2D]">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
            </div>
            <div>
              <Label className="text-[#0F1B2D]">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 border-[#E8DCC0] bg-white" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0F1B2D]">Price (\u20b9) *</Label>
                <Input type="number" min="1" step="1" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 h-10 border-[#E8DCC0] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#0F1B2D]">Image URL (optional)</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="https://\u2026" />
            </div>
            <div className="flex items-center justify-between border border-[#E8DCC0] rounded-md p-3 bg-white">
              <div>
                <div className="text-sm font-medium text-[#0F1B2D]">In stock</div>
                <p className="text-xs text-[#5a5a5a]">Turn off to hide from the public shop temporarily.</p>
              </div>
              <Switch checked={form.in_stock} onCheckedChange={(v) => setForm({ ...form, in_stock: !!v })} />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 sticky bottom-0 bg-[#F8F2E4] py-3">
              <button type="button" onClick={closeEditor} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-orange inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-70">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {submitLabel(saving, editing === 'new')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &quot;{confirmDelete?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>This product will be removed from the public /shop directory. Existing orders remain untouched.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsTab;
